import cx from 'classnames';
import * as React from 'react';

import { DatasheetGetDto } from '@common/dto/datasheet/get-dto';
import { IDatasheetComponentDto } from '@common/dto/datasheet/datasheet-component';
import { DatasheetElement, DatasheetElementId, DatasheetElementType } from '@modules/datasheets/types/datasheet-element';
import { DatasheetFieldComponentGetDto } from '@common/dto/datasheet/field-component/get-dto';

import { TextField } from './components/text-field';
import { TextElement } from './components/text-element';
import { DatasheetTextComponentGetDto } from '@common/dto/datasheet/text-component/get-dto';
import { FieldType } from '@common/dto/field/type';
import { DateField } from './components/date-field';
import { NumberField } from './components/number-field';
import { DropdownField } from './components/dropdown-field';
import { HyperlinkField } from './components/hyperlink-field';
import { LookupTableField } from './components/lookup-table-field';
import { TableField } from './components/table-field';

import './datasheet.styles.scss';
import { CheckboxField } from './components/checkbox-field';

export type DatasheetProps = {
  gridSettingsMargin: {
    show: boolean;
    width: number;
    height: number;
  };
  pageBreaks: {
    show: boolean;
    width: number;
    height: number;
  };
  datasheet: DatasheetGetDto;
  datasheetElements: Array<DatasheetElement>;
  className?: string;
  printableDatasheetHolderId: string;
  onDatasheetElementUpdate: <T extends IDatasheetComponentDto = IDatasheetComponentDto>(
    id: DatasheetElementId,
    data: DatasheetElement<T>
  ) => void;
};

export const Datasheet: React.FC<DatasheetProps> = (props) => {
  const { datasheetElements, onDatasheetElementUpdate, className, printableDatasheetHolderId } = props;

  return (
    <div className={cx("DatasheetsResultDatasheet", className)} id={printableDatasheetHolderId}>
      {datasheetElements.map((element) => {
        const fieldBasedElement = element as DatasheetElement<DatasheetFieldComponentGetDto>;
        const fieldBasedStringifiedElement = fieldBasedElement as DatasheetElement<DatasheetFieldComponentGetDto>;
        const fieldType = fieldBasedElement.original.field.type;

        if (fieldType === FieldType.checkbox) {
          return (
            <CheckboxField 
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedElement}
              onChange={onDatasheetElementUpdate}
            />
          );
        }

        if (fieldType === FieldType.table) {
          return (
            <TableField 
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedElement}
              onChange={onDatasheetElementUpdate}
            />
          );
        }

        if (fieldType === FieldType.lookupTable) {
          return (
            <LookupTableField
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedElement}
              onChange={onDatasheetElementUpdate}
            />
          );
        }

        if (fieldType === FieldType.hyperlink) {
          return (
            <HyperlinkField
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedStringifiedElement}
              onChange={onDatasheetElementUpdate}
            />
          );
        }

        if (fieldType === FieldType.dropdown) {
          return (
            <DropdownField
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedElement}
              onChange={onDatasheetElementUpdate}
            />
          );
        }

        if (
          fieldType === FieldType.number || 
          fieldType === FieldType.integer
        ) {
          return (
            <NumberField
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedElement}
              onChange={onDatasheetElementUpdate}
            />
          );
        }

        if (fieldType === FieldType.date) {
          return (
            <DateField
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedStringifiedElement}
              onChange={onDatasheetElementUpdate}
            />
          );
        }

        if (element.type === DatasheetElementType.field) {
          return (
            <TextField
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedElement}
              onChange={onDatasheetElementUpdate}
            />
          );
        }

        if (element.type === DatasheetElementType.text) {
          return (
            <TextElement
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedElement as unknown as DatasheetElement<DatasheetTextComponentGetDto>}
            />
          );
        }

        return null;
      })}
    </div>
  );
};